import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  forwardRef,
} from "react";
import confetti, { CreateTypes } from "canvas-confetti";

export type ConfettiBurst = {
  x: number;
  y: number;
  count?: number;
  angle?: number;
  spread?: number;
  startVelocity?: number;
  ticks?: number;
  colors?: string[];
  scalar?: number;
};

export type ConfettiHandle = {
  shoot: (opts: ConfettiBurst) => void;
};

const Confetti = forwardRef<ConfettiHandle, { zIndex?: number }>(
  ({ zIndex = 9999 }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const apiRef = useRef<CreateTypes | null>(null);

    useEffect(() => {
      const canvas = canvasRef.current!;
      apiRef.current = confetti.create(canvas, {
        resize: true,
        useWorker: false,
      });
      return () => {
        if (apiRef.current) {
          apiRef.current.reset();
          apiRef.current = null;
        }
      };
    }, []);

    useEffect(() => {
      const canvas = document.querySelector(
        "canvas[style*='pointer-events: none']"
      ) as HTMLCanvasElement | null;
      if (!canvas) return;

      const resize = () => {
        const ratio = window.devicePixelRatio || 1;
        canvas.width = window.innerWidth * ratio;
        canvas.height = window.innerHeight * ratio;
        canvas.style.width = "100vw";
        canvas.style.height = "100vh";
      };

      resize();
      window.addEventListener("resize", resize);
      return () => window.removeEventListener("resize", resize);
    }, []);

    useImperativeHandle(ref, () => ({
      shoot: (opts: ConfettiBurst) => {
        const api = apiRef.current;
        const canvas = canvasRef.current;
        if (!api || !canvas) return;

        const rect = canvas.getBoundingClientRect();
        const origin = {
          x: (opts.x - rect.left) / rect.width,
          y: (opts.y - rect.top) / rect.height,
        };

        api({
          particleCount: opts.count ?? 120,
          angle: opts.angle ?? 90,
          spread: opts.spread ?? 60,
          startVelocity: opts.startVelocity ?? 45,
          ticks: opts.ticks ?? 200,
          scalar: opts.scalar ?? 0.5,
          colors: opts.colors,
          origin,
          drift: 0,
          gravity: 1.1,
          shapes: ["square", "circle"],
        });
      },
    }));

    return (
      <canvas
        ref={canvasRef}
        style={{
          width: "100vw",
          height: "100vh",
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex,
        }}
      />
    );
  }
);

export default Confetti;
